import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import {
  AddCircleOutline as AddIcon,
  Close as CloseIcon,
  CheckCircleOutline as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  NotificationsOutlined as NotifIcon,
} from '@mui/icons-material';
import { NOTIF_CATEGORIES } from '../../Constants/userConstants';

export const MAX_BULK_ROWS = 50;
export const DEFAULT_BULK_ROWS = 2;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emptyBulkRow = (defaultClientId = '') => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  email: '',
  jobTitle: '',
  phone: '',
  profileId: '',
  clientId: defaultClientId,
  storeIds: [],
  notifPrefs: {},
  sent: false,
  serverError: '',
});

const ERROR_LABEL_KEYS = {
  'required-email': 'invite.bulkErrors.requiredEmail',
  'invalid-email': 'invite.bulkErrors.invalidEmail',
  'duplicate-email': 'invite.bulkErrors.duplicateEmail',
  'required-name': 'invite.bulkErrors.requiredName',
  'required-profile': 'invite.bulkErrors.requiredProfile',
};

export const getRowProfileCode = (row, profileOptions) =>
  profileOptions.find((p) => p.value === row.profileId)?.code ?? '';

export const rowNeedsClient = (row, profileOptions) => {
  const code = getRowProfileCode(row, profileOptions);
  return code === 'CLIENT_ADMIN' || code === 'USER';
};

export const rowNeedsStores = (row, profileOptions) =>
  getRowProfileCode(row, profileOptions) === 'USER';

// eslint-disable-next-line no-unused-vars -- profileOptions kept for signature parity with isBulkValid callers
export const getRowError = (row, allRows, profileOptions) => {
  const email = row.email.trim();
  if (!email) return 'required-email';
  if (!EMAIL_REGEX.test(email)) return 'invalid-email';
  const duplicateCount = allRows.filter((r) => r.email.trim().toLowerCase() === email.toLowerCase()).length;
  if (duplicateCount > 1) return 'duplicate-email';
  if (!row.name.trim()) return 'required-name';
  if (!row.profileId) return 'required-profile';
  return null;
};

export const isBulkValid = (rows, profileOptions) =>
  rows.length > 0 && rows.every((row) => !getRowError(row, rows, profileOptions));

const FIELD_HEIGHT = 32;
const fieldSx = { '& .MuiInputBase-root': { height: FIELD_HEIGHT, fontSize: 13 }, '& .MuiInputBase-input': { fontSize: 13 } };

const GRID_TEMPLATE = '1fr 1fr 150px 28px 28px 28px';

// eslint-disable-next-line react/prop-types
const NotifPrefsCell = ({ notifPrefs, categories, onChange }) => {
  const { t } = useTranslation('users');
  const [anchor, setAnchor] = useState(null);
  const hasCategories = categories.length > 0;

  return (
    <>
      <IconButton
        size="small"
        disabled={!hasCategories}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t('notificationPreferences.title')}
        sx={{ color: 'text.secondary' }}
      >
        <NotifIcon sx={{ fontSize: 17 }} />
      </IconButton>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ minWidth: 240, maxWidth: 300, p: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
            {t('notificationPreferences.title')}
          </Typography>
          <Stack spacing={0.25}>
            {categories.map((cat) => (
              <FormControlLabel
                key={cat}
                control={(
                  <Switch
                    size="small"
                    checked={!!notifPrefs[cat]}
                    onChange={(e) => onChange(cat, e.target.checked)}
                  />
                )}
                label={(
                  <Typography sx={{ fontSize: 13 }}>
                    {t(`notificationPreferences.categories.${cat}`)}
                  </Typography>
                )}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

// eslint-disable-next-line react/prop-types
const CreateUsersTable = ({ rows, onRowsChange, profileOptions, attemptedSubmit, defaultClientId = '' }) => {
  const { t } = useTranslation('users');
  const [expandedKeys, setExpandedKeys] = useState({});

  const updateRow = (key, patch) => {
    onRowsChange(rows.map((r) => (r.key === key ? { ...r, ...patch, serverError: '' } : r)));
  };

  const removeRow = (key) => onRowsChange(rows.filter((r) => r.key !== key));

  const addRow = () => {
    if (rows.length >= MAX_BULK_ROWS) return;
    onRowsChange([...rows, emptyBulkRow(defaultClientId)]);
  };

  const toggleExpand = (key) => setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));

  const lastPendingIndex = rows.reduce((acc, r, i) => (r.sent ? acc : i), -1);

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, px: 0.5, fontSize: 11, color: 'text.disabled', textTransform: 'uppercase' }}>
        <Box>{t('invite.fields.name')} *</Box>
        <Box>{t('invite.fields.email')} *</Box>
        <Box>{t('invite.fields.profile')} *</Box>
        <Box />
        <Box />
        <Box />
      </Box>

      {rows.map((row, index) => {
        if (row.sent) {
          return (
            <Box
              key={row.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, height: FIELD_HEIGHT, bgcolor: '#f0fdf4', borderRadius: '8px', fontSize: 13, color: 'success.dark' }}
            >
              <CheckIcon sx={{ fontSize: 16 }} />
              {row.email}
            </Box>
          );
        }

        const rowError = getRowError(row, rows, profileOptions);
        const displayError = row.serverError || (attemptedSubmit && rowError ? t(ERROR_LABEL_KEYS[rowError]) : '');
        const isLastRow = index === lastPendingIndex;
        const isExpanded = Boolean(expandedKeys[row.key]);
        const notifCategories = NOTIF_CATEGORIES[getRowProfileCode(row, profileOptions)] ?? [];

        return (
          <Box key={row.key}>
            <Box sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder={t('invite.placeholders.name')}
                value={row.name}
                onChange={(e) => updateRow(row.key, { name: e.target.value })}
                sx={fieldSx}
              />
              <TextField
                size="small"
                placeholder={t('invite.placeholders.email')}
                value={row.email}
                error={Boolean(displayError)}
                onChange={(e) => updateRow(row.key, { email: e.target.value })}
                sx={fieldSx}
              />
              <Select
                size="small"
                displayEmpty
                value={row.profileId}
                onChange={(e) => updateRow(row.key, { profileId: e.target.value, clientId: defaultClientId, storeIds: [] })}
                sx={{ height: FIELD_HEIGHT, fontSize: 13 }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 13 }}>
                  {t('invite.fields.profile')}
                </MenuItem>
                {profileOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
              <NotifPrefsCell
                notifPrefs={row.notifPrefs}
                categories={notifCategories}
                onChange={(cat, value) => updateRow(row.key, { notifPrefs: { ...row.notifPrefs, [cat]: value } })}
              />
              <IconButton
                size="small"
                onClick={() => toggleExpand(row.key)}
                aria-label={t(isExpanded ? 'invite.bulkCollapseRowAria' : 'invite.bulkExpandRowAria')}
                sx={{ color: 'text.secondary' }}
              >
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
              </IconButton>
              <IconButton size="small" disabled={rows.length <= 1} onClick={() => removeRow(row.key)} sx={{ color: 'error.main' }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, mt: 0.5, mb: 0.25 }}>
                <Box sx={{ pl: 2.5 }}>
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'uppercase', mb: 0.125 }}>{t('invite.fields.jobTitle')}</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t('invite.placeholders.jobTitle')}
                    value={row.jobTitle}
                    onChange={(e) => updateRow(row.key, { jobTitle: e.target.value })}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ gridColumn: '2 / span 2' }}>
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'uppercase', mb: 0.125 }}>{t('invite.fields.phone')}</Typography>
                  <MuiTelInput
                    size="small"
                    fullWidth
                    defaultCountry="PT"
                    value={row.phone}
                    onChange={(value) => updateRow(row.key, { phone: value })}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Collapse>
            {displayError && (
              <Typography sx={{ fontSize: 11, color: 'error.main', pl: 0.5, mt: 0.25 }}>{displayError}</Typography>
            )}
            {isLastRow && (
              <Box sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, mt: 0.5 }}>
                <Box sx={{ gridColumn: 6, display: 'flex', justifyContent: 'center' }}>
                  <IconButton
                    size="small"
                    aria-label={t('invite.bulkAddRowAria')}
                    disabled={rows.length >= MAX_BULK_ROWS}
                    onClick={addRow}
                    sx={{ color: 'primary.main', border: '1.5px solid', borderColor: rows.length >= MAX_BULK_ROWS ? 'action.disabled' : 'primary.main' }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default CreateUsersTable;
